import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import keyBy from 'lodash/keyBy';
// @types
import {
  IChatConversation,
  IChatSendMessage,
  IChatState,
} from 'src/@types/chat';
// utils
import {
  ChatRequests,
  CreateChannelPayload,
  UpdateParticipantRoleProps,
} from 'src/requests/chat';
import { WorkspaceRequests } from 'src/requests/workspace.requests';

const initialState: IChatState = {
  isLoading: false,
  error: null,
  contacts: { byId: {}, allIds: [] },
  conversations: { byId: {}, allIds: [] },
  activeConversationId: null,
  participants: [],
  recipients: [],
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    endLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET CONTACT SSUCCESS
    getContactsSuccess(state, action) {
      const contacts = action.payload;

      state.contacts.byId = keyBy(contacts, '_id');
      state.contacts.allIds = Object.keys(state.contacts.byId);
      state.isLoading = false;
    },

    // GET CONVERSATIONS
    getConversationsSuccess(state, action) {
      const conversations = action.payload;

      state.conversations.byId = keyBy(conversations, '_id');
      state.conversations.allIds = Object.keys(state.conversations.byId);
      state.isLoading = false;
    },

    // GET CONVERSATION

    setActiveConversation(
      state,
      action: PayloadAction<{ conversationId: string }>,
    ) {
      const { conversationId } = action.payload;
      if (!conversationId) {
        state.activeConversationId = null;
        return;
      }
      state.activeConversationId = conversationId;
    },

    setConversation(state, action: PayloadAction<IChatConversation>) {
      const conversation = action.payload;
      if (conversation.parentId) {
        state.conversations.byId[conversation.parentId] = {
          ...state.conversations.byId[conversation.parentId],
          subchannels: [
            ...(state.conversations.byId[conversation.parentId].subchannels ||
              []),
            conversation,
          ],
        };
        return;
      }
      if (state.conversations.byId[conversation._id]) {
        state.conversations.byId[conversation._id] = {
          ...state.conversations.byId[conversation._id],
          ...conversation,
        };
        return;
      }

      state.conversations.byId[conversation._id] = conversation;
      state.conversations.allIds = Object.keys(state.conversations.byId);
    },

    updateLastReadMessage(
      state,
      action: PayloadAction<{ conversationId: string; messages: string[] }>,
    ) {
      const { conversationId, messages } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation) {
        conversation.unreadMessages = conversation.unreadMessages?.filter(
          (u) => !messages.includes(u),
        );
      }
    },

    markConversationAsUnread(
      state,
      action: PayloadAction<{ conversationId: string; messages: string[] }>,
    ) {
      const { conversationId, messages } = action.payload;
      const conversation = state.conversations.byId[conversationId];
      if (conversation) {
        conversation.unreadMessages = [
          ...(conversation.unreadMessages || []),
          ...messages,
        ];
      }
    },

    // GET PARTICIPANTS
    getParticipantsSuccess(state, action) {
      state.participants = action.payload;
      state.isLoading = false;
    },

    // RESET ACTIVE CONVERSATION
    resetActiveConversation(state) {
      state.activeConversationId = null;
    },

    addRecipients(state, action) {
      const recipients = action.payload;
      state.recipients = recipients;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  addRecipients,
  resetActiveConversation,
  getParticipantsSuccess,
  setConversation,
  markConversationAsUnread,
  updateLastReadMessage,
  getConversationsSuccess,
} = slice.actions;

export function sendMessage(message: IChatSendMessage) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await ChatRequests.sendMessage(message);
      dispatch(slice.actions.endLoading());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createChannel({
  onSuccess,
  ...channel
}: CreateChannelPayload & {
  onSuccess(chat: IChatConversation): void;
}) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const newChannel = await ChatRequests.createChannel(channel);
      dispatch(slice.actions.setConversation(newChannel));
      onSuccess(newChannel);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getContacts() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const workspaceInfo = await WorkspaceRequests.getWorkspaceInfo();
      dispatch(slice.actions.getContactsSuccess(workspaceInfo.users));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getConversations() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const channels = await ChatRequests.getChannels();
      dispatch(slice.actions.getConversationsSuccess(channels));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getConversation(
  channelId: string,
  onSuccess: (channel: IChatConversation) => void,
) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const channel = await ChatRequests.getChannelById(channelId);
      dispatch(
        slice.actions.setActiveConversation({
          conversationId: channel._id,
        }),
      );
      onSuccess(channel);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function markConversationAsRead({
  conversationId,
  messages,
}: {
  conversationId: string;
  messages: string[];
}) {
  return async (dispatch: Dispatch) => {
    try {
      await ChatRequests.readMessagesFromChannel({
        channelId: conversationId,
        messages,
      });
      dispatch(
        slice.actions.updateLastReadMessage({ conversationId, messages }),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getParticipants(channelId: string) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const channel = await ChatRequests.getChannelById(channelId);
      dispatch(slice.actions.getParticipantsSuccess(channel.participants));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setPinned(
  message: Pick<IChatSendMessage, 'channel' | 'pinned'> & { id: string },
) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await ChatRequests.pinMessage(message);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setToggleThread(channelId: string, user: string) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await ChatRequests.toggleThread(channelId, user);
      getConversations();
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateParticipantRole(params: UpdateParticipantRoleProps) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const conversation = await ChatRequests.updateParticipantRole(params);
      dispatch(slice.actions.setConversation(conversation));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
